import { useEffect, useMemo, useRef, useState } from 'react'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { getDeviceCatalog, getReasons, getWorkerCatalog } from '../api/dataCorrectionApi'
import { localeCodeOf } from '../../generic/utilities/customLanguage'

// The catalogs behind the filters (RN21 bis [D9, D14]): machines from devicesBasicData and workers from
// allWorkersSimple, loaded ONCE when the screen mounts. A worker is shown as «name (code)».
const workerLabelOf = (worker) => `${worker.name} (${worker.code})`
const readWorkerName = (worker) => [worker.name, worker.surname].filter(Boolean).join(' ')

// The workers of the readings, added as they come, without repeats (RN21 bis: the fallback of the worker filter when
// allWorkersSimple is not allowed to this profile).
const addSeenWorkers = (seen, response) => {
  const next = { ...seen }
  response.timelines.forEach((block) => block.workers.forEach((worker) => {
    next[worker.id] = { id: worker.id, code: worker.code, name: readWorkerName(worker) }
  }))
  return next
}

// The machines the readings name (also the retired ones, which the catalog does not have), by id.
const addSeenDevices = (seen, response) => {
  const next = { ...seen }
  const named = [...response.devices, ...response.timelines.map((block) => block.device)]
  named.forEach((device) => { if (device.name) next[device.id] = device.name })
  return next
}

/**
 * `response` is the last reading painted: the workers it names feed the fallback of the worker filter. `canRequest`
 * (DATA_CORRECTION_REQUEST) is what makes the reasons worth asking for (RN21): without it there is no panel to use them.
 */
export const useCatalogs = (response, { canRequest }) => {
  const { i18n } = useSmartTranslation()
  const [devices, setDevices] = useState([])
  const [workers, setWorkers] = useState([])
  const [workersFailed, setWorkersFailed] = useState(false)
  const [devicesMessage, setDevicesMessage] = useState([])
  const [reasons, setReasons] = useState([])
  const [reasonsMessage, setReasonsMessage] = useState([])
  const [reasonsFailed, setReasonsFailed] = useState(false)
  const [seenWorkers, setSeenWorkers] = useState({})
  const [seenDevices, setSeenDevices] = useState({})
  const catalogsAliveRef = useRef(false)
  const reasonsAliveRef = useRef(false)

  useEffect(() => {
    // The guard is switched on INSIDE the effect: StrictMode mounts, unmounts and mounts again, and a guard that is only
    // switched off in the cleanup would stay off for the second mount.
    catalogsAliveRef.current = true
    getDeviceCatalog()
      .then((answer) => { if (catalogsAliveRef.current && Array.isArray(answer)) setDevices(answer) })
      // Without machines the filter stays empty and the screen can still be asked by worker (RN21 bis).
      .catch(() => { if (catalogsAliveRef.current) setDevicesMessage([{ key: 'Unexpected error ({{code}}).', params: { code: 'devicesBasicData' } }]) })
    getWorkerCatalog()
      .then((answer) => { if (catalogsAliveRef.current && Array.isArray(answer)) setWorkers(answer) })
      .catch(() => { if (catalogsAliveRef.current) setWorkersFailed(true) })
    return () => { catalogsAliveRef.current = false }
  }, [])

  useEffect(() => {
    if (!canRequest) return undefined
    reasonsAliveRef.current = true
    getReasons()
      .then((answer) => { if (reasonsAliveRef.current && Array.isArray(answer)) setReasons(answer) })
      .catch(() => {
        if (!reasonsAliveRef.current) return
        setReasonsFailed(true)
        setReasonsMessage([{ key: 'The reasons could not be loaded, so corrections cannot be applied.', params: {} }])
      })
    return () => { reasonsAliveRef.current = false }
  }, [canRequest])

  useEffect(() => {
    if (!response) return
    setSeenWorkers((seen) => addSeenWorkers(seen, response))
    setSeenDevices((seen) => addSeenDevices(seen, response))
  }, [response])

  const language = localeCodeOf(i18n.language)
  const deviceOptions = useMemo(
    () => devices.map((device) => ({ id: device.id, label: device.name })).sort((a, b) => a.label.localeCompare(b.label, language)),
    [devices, language]
  )
  const workerOptions = useMemo(() => {
    const source = workersFailed ? Object.values(seenWorkers) : workers
    return source.map((worker) => ({ id: worker.id, label: workerLabelOf(worker) })).sort((a, b) => a.label.localeCompare(b.label, language))
  }, [workers, workersFailed, seenWorkers, language])

  // A machine's name for a message (RN77): the catalog first, then what an earlier reading said, '' if nobody knows.
  const deviceNameOf = useMemo(() => {
    const fromCatalog = Object.fromEntries(devices.map((device) => [device.id, device.name]))
    return (id) => fromCatalog[id] || seenDevices[id] || ''
  }, [devices, seenDevices])

  // A worker's name for a message (RN77): the catalog first (`name` as Sun gives it), then what the readings said, '' if nobody knows.
  const workerNameOf = useMemo(() => {
    const fromCatalog = Object.fromEntries(workers.map((worker) => [worker.id, worker.name]))
    return (id) => fromCatalog[id] || (seenWorkers[id] && seenWorkers[id].name) || ''
  }, [workers, seenWorkers])

  return { deviceOptions, workerOptions, deviceNameOf, workerNameOf, reasons, reasonsFailed, messages: [...devicesMessage, ...reasonsMessage] }
}
